import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type FeedbackEntry } from "../lib/api";
import { fmtAgo } from "../lib/format";
import { remembered } from "../lib/remember";

/**
 * Every entry is shown WITH what was done about it. That pairing is the feature, not a nicety:
 * feedback that visibly goes nowhere stops arriving. A handling record nobody sees is the same as
 * no handling record, so the two are never rendered apart.
 */
function Handling({ entry }: { entry: FeedbackEntry }) {
  const qc = useQueryClient();
  const [author, setAuthor] = useState(remembered("feedback.author"));
  const [body, setBody] = useState("");
  const add = useMutation({
    mutationFn: () => api.addHandling(entry.id, { author, body }),
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: ["feedback"] });
    },
  });

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--line)", paddingTop: 8 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        what was done{entry.handling.length === 0 ? " — nothing yet" : ""}
      </div>
      {entry.handling.map((h) => (
        <div key={h.id} style={{ marginBottom: 6 }}>
          <span className="muted" style={{ fontSize: 11 }}>
            {h.author} · {fmtAgo(h.created_ts)}
          </span>
          <div>{h.body}</div>
        </div>
      ))}
      {add.isError && <div className="error-bar">{String(add.error)}</div>}
      <div className="row" style={{ gap: 6, marginTop: 6 }}>
        <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="you" style={{ width: 110 }} />
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="what you did about it"
          style={{ flex: 1 }}
        />
        <button disabled={!author.trim() || !body.trim() || add.isPending} onClick={() => add.mutate()}>
          add
        </button>
      </div>
    </div>
  );
}

function Entry({ e }: { e: FeedbackEntry }) {
  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <strong>{e.intention}</strong>
        <span className="muted" style={{ fontSize: 12 }}>
          {e.author} · {fmtAgo(e.created_ts)}
        </span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        run <Link to={`/d/${encodeURIComponent(e.device_id ?? "")}/runs/${encodeURIComponent(e.run_id)}`}>{e.run_id}</Link>
        {e.promoted_to_request && <> · became feature request #{e.promoted_to_request}</>}
      </div>
      <dl className="kv" style={{ marginTop: 8 }}>
        {e.expectation && (
          <>
            <dt>expected</dt>
            <dd>{e.expectation}</dd>
          </>
        )}
        {e.what_happened && (
          <>
            <dt>happened</dt>
            <dd>{e.what_happened}</dd>
          </>
        )}
        {/* Labelled as the author's guess wherever it is shown, so nobody downstream reads it as a
            finding. We are the authority on causes; they are the authority on what they saw. */}
        {e.what_went_wrong && (
          <>
            <dt>their guess</dt>
            <dd>{e.what_went_wrong}</dd>
          </>
        )}
      </dl>
      {e.attachments?.length > 0 && (
        <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {e.attachments.map((a) => (
            <a key={a.id} href={`/api/feedback/attachments/${a.id}`} target="_blank" rel="noreferrer">
              <img
                src={`/api/feedback/attachments/${a.id}`}
                alt="screenshot the author attached"
                style={{ maxWidth: 220, maxHeight: 220, borderRadius: 4, border: "1px solid var(--line)" }}
              />
            </a>
          ))}
        </div>
      )}
      <Handling entry={e} />
    </div>
  );
}

export function Feedback() {
  const q = useQuery({ queryKey: ["feedback"], queryFn: () => api.feedback() });

  return (
    <div>
      <h2>feedback</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        What testers meant, expected and saw — the things a trace cannot show, because a trace is
        the run's own account of itself. Written by people, for people.
      </p>
      {q.isError && <div className="error-bar">{String(q.error)}</div>}
      {q.isLoading && <div className="muted">loading…</div>}
      {q.data?.length === 0 && (
        <div className="panel muted">
          No entries yet. Open a run and use “tell us about this run”.
        </div>
      )}
      {q.data?.map((e) => (
        <Entry key={e.id} e={e} />
      ))}
    </div>
  );
}
